'use client';

/**
 * OrgDisciplineContext — GLOBAL, discipline-first + optional org.
 *
 * Priority order (DISCIPLINE drives everything):
 *   1. Discipline (required, primary) — options from GET /api/sport-disciplines/with-implementation (has_implementation=true).
 *      - For administrator: all implemented disciplines.
 *      - For organization_administrator: union of their orgs' disciplines (falls back to all if no org disciplines).
 *   2. Organization (optional — "Tutte le associazioni" = null) — options from GET /api/organizations/mine.
 *      When a specific org is chosen, the discipline list is narrowed to that org's disciplines.
 *   3. Squad type — derived from the selected discipline plugin.
 *
 * Persistence: all three selections survive page reload via localStorage.
 * Auto-select: each level auto-selects when exactly one option is available.
 * Safe defaults: never crashes when no discipline is selected.
 *
 * ⛔ Il contesto che accompagna le chiamate sta in `requestScope.ts`, letto in
 * modo sincrono PRIMA degli effetti delle pagine; un contesto rifiutato dal
 * backend (o salvato e non piu' disponibile) si mostra con il suo motivo e si
 * azzera solo per scelta dell'utente (`contextRejection`, `resetRejectedContext`).
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
  useRef,
} from 'react';
import { useAuth } from './StaticAuthContext';
import {
  DISCIPLINE_STORAGE_KEY,
  ORGANIZATION_STORAGE_KEY,
  SQUAD_TYPE_STORAGE_KEY,
  clearContextRejection,
  currentRequestScope,
  installScopedFetch,
  readStorage,
  reportContextRejection,
  setRequestScope,
  subscribeToContextRejection,
  writeStorage,
  type ContextRejection,
} from './requestScope';
import { getPluginAsync } from '../plugins/disciplines/unified-registry';
import type { SquadTypeConfig } from '../plugins/disciplines/types';
import type { SportDisciplineResponse, SportDisciplineWithImplementation } from '../types/sport-discipline';

// ---------------------------------------------------------------------------
// Org type
// ---------------------------------------------------------------------------
export interface OrgOption {
  id: string;
  name: string;
  short_name?: string | null;
  organization_type?: string;
}

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------
export interface OrgDisciplineContextValue {
  // ---- disciplines (PRIMARY) ----
  /** All disciplines with a plugin implementation available to this user. */
  disciplines: SportDisciplineResponse[];
  disciplinesLoading: boolean;
  disciplinesError: string | null;

  selectedDisciplineCode: string | null;
  setSelectedDisciplineCode: (code: string | null) => void;
  selectedDiscipline: SportDisciplineResponse | null;

  // ---- orgs (OPTIONAL) ----
  orgs: OrgOption[];
  orgsLoading: boolean;
  orgsError: string | null;

  /** null = "Tutte le associazioni" (no filter by org) */
  selectedOrgId: string | null;
  setSelectedOrgId: (id: string | null) => void;
  selectedOrg: OrgOption | null;

  // ---- squad types ----
  squadTypes: SquadTypeConfig[];
  squadTypesLoading: boolean;

  selectedSquadTypeCode: string | null;
  setSelectedSquadTypeCode: (code: string | null) => void;
  selectedSquadType: SquadTypeConfig | null;

  // ---- contesto rifiutato ----
  /** Il motivo per cui il contesto salvato non vale (dal backend, o perche'
   * non e' tra le scelte disponibili); null se va tutto bene. */
  contextRejection: ContextRejection | null;
  /** Azzera la parte rifiutata del contesto e ricarica la pagina. */
  resetRejectedContext: () => void;
}

const OrgDisciplineContext = createContext<OrgDisciplineContextValue | undefined>(undefined);

const LS_ORG_KEY = ORGANIZATION_STORAGE_KEY;
const LS_DISC_KEY = DISCIPLINE_STORAGE_KEY;
const LS_SQUAD_KEY = SQUAD_TYPE_STORAGE_KEY;
const readLS = readStorage;
const writeLS = writeStorage;

/** Dopo l'azzeramento di un contesto rifiutato le pagine rileggono tutto da
 * capo: le loro prime letture erano state rifiutate. Sostituibile nei test. */
export const contextPageActions = {
  reload: () => {
    try {
      window.location.reload();
    } catch {
      // Ambiente senza navigazione (test): il contesto e' comunque azzerato.
    }
  },
};

// Testi di un rifiuto deciso QUI: la scelta salvata non e' tra quelle che il
// backend ha appena elencato (le organizzazioni dell'utente, le discipline).
const STORED_ORGANIZATION_UNAVAILABLE =
  "L'organizzazione salvata in questo browser non è tra quelle a cui hai accesso.";
const STORED_DISCIPLINE_UNAVAILABLE =
  'La disciplina salvata in questo browser non è disponibile in questo contesto.';

const AUTH_HEADERS: Record<string, string> = {
  'Content-Type': 'application/json',
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
export function OrgDisciplineProvider({ children }: { children: ReactNode }) {
  const { authState } = useAuth();
  const userId =
    authState.user?.user_id ??
    (authState.user?.['id' as keyof typeof authState.user] as string | undefined) ??
    '';
  const privilege = authState.user?.privilege ?? 'player';

  // ⛔ PRIMA di qualunque effetto delle pagine (gli effetti dei figli girano
  // prima di quelli del genitore): installata qui, nel render, la patch e'
  // gia' attiva quando le pagine fanno le loro prime letture. Idempotente.
  installScopedFetch();

  // ---- discipline state (PRIMARY) ----
  const [allDisciplines, setAllDisciplines] = useState<SportDisciplineWithImplementation[]>([]);
  const [disciplinesLoading, setDisciplinesLoading] = useState(false);
  const [disciplinesError, setDisciplinesError] = useState<string | null>(null);
  // SSR-safe: init a null (uguale su server e primo render client per evitare
  // hydration mismatch). I valori persistiti si caricano da localStorage nel
  // useEffect di mount qui sotto.
  const [selectedDisciplineCode, setSelectedDisciplineCodeRaw] = useState<string | null>(null);

  // ---- org state (OPTIONAL) ----
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [orgsError, setOrgsError] = useState<string | null>(null);
  const [selectedOrgId, setSelectedOrgIdRaw] = useState<string | null>(null);

  // ---- org-scoped disciplines (used when a specific org is selected) ----
  const [orgDisciplineCodes, setOrgDisciplineCodes] = useState<Set<string> | null>(null);
  const [orgDisciplinesLoading, setOrgDisciplinesLoading] = useState(false);
  const [orgDisciplinesError, setOrgDisciplinesError] = useState<string | null>(null);

  // ---- squad type state ----
  const [squadTypes, setSquadTypes] = useState<SquadTypeConfig[]>([]);
  const [squadTypesLoading, setSquadTypesLoading] = useState(false);
  const [selectedSquadTypeCode, setSelectedSquadTypeCodeRaw] = useState<string | null>(null);

  // ---- contesto rifiutato ----
  const [contextRejection, setContextRejection] = useState<ContextRejection | null>(null);
  // La disciplina selezionata viene da quanto era salvato (non da una scelta
  // fatta ora): se non e' disponibile lo si dice, non la si toglie in silenzio.
  const disciplineFromStorageRef = useRef(true);

  // Lo STATO che disegna i selettori si allinea dopo il mount (client), cosi'
  // server e primo render client coincidono (tutte null) → niente hydration
  // mismatch. Le chiamate non aspettano questo effetto: il contesto lo leggono
  // da `requestScope`, gia' pronto dal primo render (link condiviso
  // `?org=...&disciplina=...` compreso).
  useEffect(() => {
    const initial = currentRequestScope();
    const s = readLS(LS_SQUAD_KEY);
    if (initial.disciplineCode) setSelectedDisciplineCodeRaw(initial.disciplineCode);
    if (initial.orgId) setSelectedOrgIdRaw(initial.orgId);
    if (s) setSelectedSquadTypeCodeRaw(s);
  }, []);

  useEffect(() => subscribeToContextRejection(setContextRejection), []);

  // ---------------------------------------------------------------------------
  // Setters with localStorage sync
  // ---------------------------------------------------------------------------
  const setSelectedDisciplineCode = useCallback((code: string | null) => {
    disciplineFromStorageRef.current = false;
    setSelectedDisciplineCodeRaw(code);
    writeLS(LS_DISC_KEY, code);
    setRequestScope({ disciplineCode: code });
    setContextRejection((current) => (current?.part === 'discipline' ? null : current));
    // Cascade-clear squad type when discipline changes
    setSelectedSquadTypeCodeRaw(null);
    writeLS(LS_SQUAD_KEY, null);
    setSquadTypes([]);
  }, []);

  const setSelectedOrgId = useCallback((id: string | null) => {
    // Cambiare organizzazione puo' restringere le discipline: quella scelta
    // prima, se sparisce, sparisce per una scelta fatta ora.
    disciplineFromStorageRef.current = false;
    setSelectedOrgIdRaw(id);
    writeLS(LS_ORG_KEY, id);
    setRequestScope({ orgId: id });
    setContextRejection((current) => (current?.part === 'organization' ? null : current));
    // Org change may narrow discipline list — reset org discipline codes so they reload
    setOrgDisciplineCodes(null);
    setOrgDisciplinesError(null);
  }, []);

  const setSelectedSquadTypeCode = useCallback((code: string | null) => {
    setSelectedSquadTypeCodeRaw(code);
    writeLS(LS_SQUAD_KEY, code);
  }, []);

  // ---------------------------------------------------------------------------
  // Fetch all disciplines with plugin implementation
  // ---------------------------------------------------------------------------
  const fetchAllDisciplines = useCallback(async () => {
    setDisciplinesLoading(true);
    setDisciplinesError(null);
    try {
      const res = await fetch('/api/sport-disciplines/with-implementation', {
        headers: AUTH_HEADERS,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list: SportDisciplineWithImplementation[] = (
        Array.isArray(data) ? data : data.disciplines ?? data.data ?? []
      ).filter((d: SportDisciplineWithImplementation) => d.has_implementation && d.is_active);
      setAllDisciplines(list);
    } catch (err) {
      setDisciplinesError(
        err instanceof Error ? err.message : 'Errore nel caricamento discipline'
      );
    } finally {
      setDisciplinesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllDisciplines();
  }, [fetchAllDisciplines]);

  // ---------------------------------------------------------------------------
  // Fetch orgs
  // ---------------------------------------------------------------------------
  const fetchOrgs = useCallback(async () => {
    if (!userId) return;
    setOrgsLoading(true);
    setOrgsError(null);
    try {
      const params = new URLSearchParams({ user_id: String(userId), privilege: String(privilege) });
      const res = await fetch(`/api/organizations/mine?${params}`, {
        headers: AUTH_HEADERS,
      });
      if (!res.ok) {
        // Fallback for administrators: fetch all orgs
        if (privilege === 'administrator') {
          const fallback = await fetch('/api/organizations', { headers: AUTH_HEADERS });
          if (fallback.ok) {
            const data = await fallback.json();
            setOrgs(
              (data.organizations || data || []).map((o: Record<string, unknown>) => ({
                id: String(o.id),
                name: String(o.name),
                short_name: o.short_name as string | null,
                organization_type: o.organization_type as string,
              }))
            );
            return;
          }
        }
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      setOrgs(
        (data.organizations || data || []).map((o: Record<string, unknown>) => ({
          id: String(o.id),
          name: String(o.name),
          short_name: o.short_name as string | null,
          organization_type: o.organization_type as string,
        }))
      );
    } catch (err) {
      setOrgsError(err instanceof Error ? err.message : 'Errore nel caricamento organizzazioni');
    } finally {
      setOrgsLoading(false);
    }
  }, [userId, privilege]);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  // Auto-select org when exactly one available
  useEffect(() => {
    if (orgs.length === 1 && !selectedOrgId) {
      setSelectedOrgId(orgs[0].id);
    }
  }, [orgs, selectedOrgId, setSelectedOrgId]);

  // ⛔ L'organizzazione salvata non e' tra le sue: lo si DICE, non la si toglie
  // in silenzio. Il contesto si azzera quando l'utente lo chiede
  // (`resetRejectedContext`) o ne sceglie un'altra.
  useEffect(() => {
    if (selectedOrgId && orgs.length > 0) {
      const found = orgs.find((o) => o.id === selectedOrgId);
      if (!found) {
        reportContextRejection({ part: 'organization', error: STORED_ORGANIZATION_UNAVAILABLE });
      }
    }
  }, [orgs, selectedOrgId]);

  // ---------------------------------------------------------------------------
  // When an org is selected, fetch its discipline codes to narrow the list
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!selectedOrgId) {
      setOrgDisciplineCodes(null);
      setOrgDisciplinesError(null);
      return;
    }
    let cancelled = false;
    setOrgDisciplinesLoading(true);
    setOrgDisciplinesError(null);
    // La rotta corretta e' /organizations/{id}/disciplines (plurale, con suffisso):
    // quella usata prima non esisteva e rispondeva sempre 404, cosi' il
    // restringimento cadeva nella degradazione qui sotto e ogni organizzazione
    // mostrava TUTTE le discipline. Sceglierne una non abilitata porta ora a un
    // 403 dal backend, quindi l'elenco deve essere quello vero.
    //
    // ⛔ NESSUN ripiego su «tutte le discipline» quando la chiamata fallisce:
    // un ripiego trasforma un errore in un elenco sbagliato, e un'associazione
    // che pratica solo Bridge tornava a vedere ogni disciplina del sistema.
    // Un errore si mostra (vedi `disciplinesError`), non si nasconde dietro
    // un elenco che sembra completo e non lo è.
    fetch(`/api/sport-disciplines/organizations/${selectedOrgId}/disciplines`, { headers: AUTH_HEADERS })
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          setOrgDisciplineCodes(new Set());
          setOrgDisciplinesError(
            `Errore nel caricamento delle discipline dell'organizzazione (HTTP ${res.status})`
          );
          return;
        }
        const data = await res.json();
        const list: SportDisciplineResponse[] = Array.isArray(data)
          ? data
          : data.disciplines ?? data.data ?? [];
        if (!cancelled) {
          setOrgDisciplineCodes(new Set(list.map((d) => d.code)));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setOrgDisciplineCodes(new Set());
          setOrgDisciplinesError(
            err instanceof Error ? err.message : "Errore nel caricamento delle discipline dell'organizzazione"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setOrgDisciplinesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedOrgId]);

  // ---------------------------------------------------------------------------
  // Derived: discipline list visible to user
  // orgDisciplineCodes narrows when a specific org is selected and disciplines loaded.
  // If narrowing yields 0 (no overlap, and the fetch actually succeeded), fall
  // back to full list — quella è una risposta valida che non trova sovrapposizioni,
  // non un errore.
  //
  // ⛔ Un errore nel caricamento NON ricade in questo ramo: senza saperlo per
  // certo, mostrare "tutte le discipline" è di nuovo il ripiego vietato. Qui
  // si mostra un elenco vuoto e l'errore si legge da `disciplinesError`.
  // ---------------------------------------------------------------------------
  const disciplines: SportDisciplineResponse[] = (() => {
    if (!selectedOrgId || orgDisciplinesLoading) {
      return allDisciplines;
    }
    if (orgDisciplinesError) {
      return [];
    }
    if (!orgDisciplineCodes) {
      return allDisciplines;
    }
    const narrowed = allDisciplines.filter((d) => orgDisciplineCodes.has(d.code));
    return narrowed.length > 0 ? narrowed : allDisciplines;
  })();

  // Auto-select discipline when exactly one available
  useEffect(() => {
    if (disciplines.length === 1 && !selectedDisciplineCode) {
      setSelectedDisciplineCode(disciplines[0].code);
    }
  }, [disciplines, selectedDisciplineCode, setSelectedDisciplineCode]);

  // Disciplina non disponibile: se era quella salvata lo si dice (come per
  // l'organizzazione); se e' sparita per un cambio di organizzazione appena
  // fatto, si toglie — e' la conseguenza della scelta dell'utente.
  useEffect(() => {
    if (selectedDisciplineCode && disciplines.length > 0) {
      const found = disciplines.find((d) => d.code === selectedDisciplineCode);
      if (found) return;
      if (disciplineFromStorageRef.current) {
        reportContextRejection({ part: 'discipline', error: STORED_DISCIPLINE_UNAVAILABLE });
      } else {
        setSelectedDisciplineCode(null);
      }
    }
  }, [disciplines, selectedDisciplineCode, setSelectedDisciplineCode]);

  // ---------------------------------------------------------------------------
  // Resolve squad types from plugin for selected discipline
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!selectedDisciplineCode) {
      setSquadTypes([]);
      return;
    }
    let cancelled = false;
    setSquadTypesLoading(true);
    getPluginAsync(selectedDisciplineCode)
      .then((plugin) => {
        if (cancelled || !plugin) return;
        const types = plugin.getSquadTypes?.() ?? [];
        setSquadTypes(types);
      })
      .catch(() => {
        if (!cancelled) setSquadTypes([]);
      })
      .finally(() => {
        if (!cancelled) setSquadTypesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDisciplineCode]);

  // Auto-select squad type when exactly one
  useEffect(() => {
    if (squadTypes.length === 1 && !selectedSquadTypeCode) {
      setSelectedSquadTypeCode(squadTypes[0].code);
    }
  }, [squadTypes, selectedSquadTypeCode, setSelectedSquadTypeCode]);

  // Validate stored squad type still in list
  useEffect(() => {
    if (selectedSquadTypeCode && squadTypes.length > 0) {
      const found = squadTypes.find((st) => st.code === selectedSquadTypeCode);
      if (!found) setSelectedSquadTypeCode(null);
    }
  }, [squadTypes, selectedSquadTypeCode, setSelectedSquadTypeCode]);

  // ---------------------------------------------------------------------------
  // Derived objects
  // ---------------------------------------------------------------------------
  const selectedOrg = orgs.find((o) => o.id === selectedOrgId) ?? null;
  const selectedDiscipline = disciplines.find((d) => d.code === selectedDisciplineCode) ?? null;
  const selectedSquadType = squadTypes.find((st) => st.code === selectedSquadTypeCode) ?? null;

  const resetRejectedContext = useCallback(() => {
    const part = contextRejection?.part;
    if (part === 'organization') setSelectedOrgId(null);
    if (part === 'discipline') setSelectedDisciplineCode(null);
    clearContextRejection();
    setContextRejection(null);
    contextPageActions.reload();
  }, [contextRejection, setSelectedOrgId, setSelectedDisciplineCode]);

  const value: OrgDisciplineContextValue = {
    disciplines,
    disciplinesLoading: disciplinesLoading || orgDisciplinesLoading,
    // L'errore del restringimento per organizzazione conta quanto quello del
    // caricamento generale: in entrambi i casi l'elenco mostrato non è
    // affidabile, e chi legge deve saperlo — non vedersi un elenco silenzioso.
    disciplinesError: disciplinesError ?? orgDisciplinesError,
    selectedDisciplineCode,
    setSelectedDisciplineCode,
    selectedDiscipline,

    orgs,
    orgsLoading,
    orgsError,
    selectedOrgId,
    setSelectedOrgId,
    selectedOrg,

    squadTypes,
    squadTypesLoading,
    selectedSquadTypeCode,
    setSelectedSquadTypeCode,
    selectedSquadType,

    contextRejection,
    resetRejectedContext,
  };

  // La propagazione del contesto al backend (le intestazioni su ogni chiamata
  // `/api/`) e la lettura dei rifiuti stanno in `requestScope.ts`.

  return (
    <OrgDisciplineContext.Provider value={value}>{children}</OrgDisciplineContext.Provider>
  );
}

export function useOrgDiscipline(): OrgDisciplineContextValue {
  const ctx = useContext(OrgDisciplineContext);
  if (!ctx) {
    throw new Error('useOrgDiscipline must be used within an OrgDisciplineProvider');
  }
  return ctx;
}
